import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { IListResponseData, TrainingFormat, TrainingType } from '@pro-connect/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { ITrainingsList } from '../../models/trainings-list';
import { ITrainingsListDto } from '../../models/trainings-list-dto';
import { TrainingsListService } from './trainings-list.service';
import { IPlace } from '../../models/place';

export class TrainingsListStateGetCategories {
  public static readonly type = '[TrainingsListState] get categories';
}

export class TrainingsListStateGetSubCategories {
  public static readonly type = '[TrainingsListState] get subcategories';

  constructor(public payload: { categoriesIds: number[] }) {}
}

export class TrainingsListStateGetTrainings {
  public static readonly type = '[TrainingsListState] get trainings';

  public constructor(public readonly payload: { listDto: ITrainingsListDto }) {}
}

export class TrainingsListStateGetPlaces {
  public static readonly type = '[TrainingsListState] get places';
}

export class TrainingsListStateGetCities {
  public static readonly type = '[TrainingsListState] get cities';
}

export class TrainingsListStateGetEducators {
  public static readonly type = '[TrainingsListState] get educators';
}

export class TrainingsListStateNullifyInit {
  public static readonly type = '[TrainingsListState] nullify init';
}

export interface TrainingsListStateModel {
  loading: boolean;
  listDto: ITrainingsListDto;
  list: IListResponseData<ITrainingsList> | null;
  categories: { id: number; name: string }[];
  subCategories: { categoryId: number; categoryName: string; subCategories: { id: number; name: string }[] }[];
  places: IPlace[];
  cities: string[];
  educators: any[];
  maxRangeValue: number | null;
}

@State<TrainingsListStateModel>({
  name: 'trainingsList',
  defaults: {
    loading: false,
    listDto: {
      perPage: 6,
      page: 1,
      direction: 'desc',
      sort: 'id',
      training_type: null,
      brand: null,
      category_id: null,
      format: null,
      search: null,
      target: 'none',
      date_from: null,
      date_to: null,
    },
    maxRangeValue: null,
    list: null,
    categories: [],
    subCategories: [],
    places: [],
    educators: [],
    cities: [],
  },
})
@Injectable()
export class TrainingsListState {
  public constructor(private readonly _trainingsListService: TrainingsListService) {}

  @Selector()
  public static loading({ loading }: TrainingsListStateModel): boolean {
    return loading;
  }

  @Selector()
  public static list({ list }: TrainingsListStateModel): IListResponseData<ITrainingsList> | null {
    return list;
  }

  @Selector()
  public static totalPages({ list }: TrainingsListStateModel): number {
    return Number(list?.total_pages);
  }

  @Selector()
  public static categories({ categories }: TrainingsListStateModel): { id: number; name: string }[] {
    return categories;
  }

  @Selector()
  public static subCategories({ subCategories }: TrainingsListStateModel): {
    categoryId: number;
    categoryName: string;
    subCategories: { id: number; name: string }[];
  }[] {
    return subCategories;
  }

  @Selector()
  public static places({ places }: TrainingsListStateModel): IPlace[] {
    return places;
  }

  @Selector()
  public static cities({ cities }: TrainingsListStateModel): string[] {
    return cities;
  }

  @Selector()
  public static educators({ educators }: TrainingsListStateModel): any[] {
    return educators;
  }

  @Selector()
  public static trainingType({ listDto }: TrainingsListStateModel): TrainingType | null {
    return listDto.training_type ? listDto.training_type : null;
  }

  @Selector()
  public static maxRangeValue({ maxRangeValue }: TrainingsListStateModel): number | null {
    return maxRangeValue;
  }

  @Action(TrainingsListStateGetTrainings)
  public getTrainings(
    { getState, patchState }: StateContext<TrainingsListStateModel>,
    { payload: { listDto } }: TrainingsListStateGetTrainings,
  ): Observable<IListResponseData<ITrainingsList>> {
    const { maxRangeValue } = getState();
    patchState({
      loading: true,
      listDto: {
        ...getState().listDto,
        ...listDto,
      },
    });
    return this._trainingsListService.getTrainings(listDto).pipe(
      tap((data: IListResponseData<ITrainingsList>) => {
        patchState({
          list: {
            ...data,
            data:
              listDto.training_type === TrainingType.EVENT
                ? [
                    ...this._addCitiesEvents(),
                    ...data.data.map((el: ITrainingsList) => ({ ...el, price: Math.round(el.price) })),
                  ]
                : [...data.data.map((el: ITrainingsList) => ({ ...el, price: Math.round(el.price) }))],
          },
          maxRangeValue: !maxRangeValue
            ? Math.max(...data.data.map((el: ITrainingsList) => Math.round(el.price)))
            : maxRangeValue,
        });
      }),

      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }

  @Action(TrainingsListStateGetCategories)
  public getCategories({
    patchState,
    getState,
  }: StateContext<TrainingsListStateModel>): Observable<{ id: number; name: string }[]> {
    const { categories, subCategories } = getState();

    return of(categories).pipe(
      switchMap((categoriesValue: { id: number; name: string }[]) => {
        return categoriesValue.length ? of(categoriesValue) : this._trainingsListService.getCategories();
      }),
      switchMap((categories: { id: number; name: string }[]) => {
        patchState({
          categories: [...categories],
        });
        const arrReq: Observable<any>[] = [];

        const actualSubCategories = getState().subCategories;
        if (!actualSubCategories.length) {
          categories.forEach((a: { id: number; name: string }) => {
            arrReq.push(this._trainingsListService.getSubCategoriesByCategoryId(a.id));
          });
        }
        return arrReq.length > 0 ? combineLatest(arrReq) : of([]);
      }),
      tap((subCat: any) => {
        const actualCategories = getState().categories;
        const actualSubCategories = getState().subCategories;

        if (!actualSubCategories.length) {
          const readySubCategoresArray: {
            categoryId: number;
            categoryName: string;
            subCategories: { id: number; name: string }[];
          }[] = [];

          actualCategories.forEach((el: { id: number; name: string }, index) => {
            readySubCategoresArray.push({
              categoryName: el.name,
              categoryId: el.id,
              subCategories: subCat[index],
            });
          });

          patchState({
            subCategories: readySubCategoresArray,
          });
        }
      }),
    );
  }

  // @Action(TrainingsListStateGetSubCategories)
  // public getSubCategories(
  //   { patchState, getState }: StateContext<TrainingsListStateModel>,
  //   { payload: { categoriesIds } }: TrainingsListStateGetSubCategories,
  // ): Observable<{ id: number; name: string }[]> {
  //   return this._trainingsListService.getSubCategories(categoriesIds).pipe(
  //     tap((subCategories: { id: number; name: string }[]) => {
  //       patchState({
  //         subCategories,
  //       });
  //     }),
  //   );
  // }
  @Action(TrainingsListStateGetPlaces)
  public getPlaces({ patchState, getState }: StateContext<TrainingsListStateModel>): Observable<IPlace[]> {
    const { places } = getState();

    return of(places).pipe(
      switchMap((placesValue: IPlace[]) => {
        return placesValue.length ? of(placesValue) : this._trainingsListService.getTrainingsPlaces();
      }),
      tap((places: IPlace[]) => {
        patchState({
          places: [...places],
        });
      }),
    );
  }

  @Action(TrainingsListStateGetPlaces)
  public getCities({ patchState, getState }: StateContext<TrainingsListStateModel>): Observable<string[]> {
    const { cities } = getState();

    return of(cities).pipe(
      switchMap((citiesValue: string[]) => {
        return citiesValue.length ? of(citiesValue) : this._trainingsListService.getTrainingsCities();
      }),
      tap((cities: string[]) => {
        patchState({
          cities: [...cities],
        });
      }),
    );
  }

  @Action(TrainingsListStateGetEducators)
  public getEducators({ patchState, getState }: StateContext<TrainingsListStateModel>): Observable<any[]> {
    const { educators } = getState();

    return of(educators).pipe(
      switchMap((educatorsValue: any[]) => {
        return educatorsValue.length ? of(educatorsValue) : this._trainingsListService.getTrainingsEducators();
      }),
      tap((educators: any[]) => {
        patchState({
          educators: [...educators],
        });
      }),
    );
  }

  private _addCitiesEvents(): ITrainingsList[] {
    return [
      {
        format: TrainingFormat.OFFLINE,
        list_photo: 'assets/images/pages/cities/madrid-city.webp',
        name: 'Madryt',
        payment: 'paid',
        price: 0,
        slug: 'madryt',
        training_type: TrainingType.EVENT,
      },
      {
        format: TrainingFormat.OFFLINE,
        list_photo: 'assets/images/pages/cities/singapur-city.webp',
        name: 'Singapur',
        payment: 'paid',
        price: 0,
        slug: 'singapur',
        training_type: TrainingType.EVENT,
      },
    ];
  }
}
